"use client"

import React, { useCallback, useState } from 'react';
import Link from 'next/link';
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/fun-ui/Button";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Paper } from "@/stores/slices/paperSlice"
import { useFetchPapers, useStore, useSubjects } from "@/stores/appStore"
import { DeleteIcon, RenameIcon, RemoveIcon, AIQAIcon, BilingualIcon } from "@/components/fun-ui/icons";
import { Tip } from '@/components/fun-ui/Tip';
import ModifyPaperInfoButton from '../ModifyPaperInfoButton';
import { useRouter } from 'next/navigation';
import { PaperBadge } from '../PaperBadge';

export const PaperInformation = ({
  paper, subjectId, className = '',
}: {
  paper: Paper,
  subjectId: string,
  className?: string;
}) => {
  const [deletePaper] = useStore((state) => [
    state.deletePaper
  ]);
  const {
    fetchPapers,
  } = useFetchPapers();
  const [paperName, setPaperName] = useState(paper.extra.title || paper.paperName);
  const { removePaper } = useSubjects();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const linkClassName = "p-0 text-xs text-muted-foreground hover:text-primary hover:no-underline h-auto gap-1";

  const handleRemovePaper = () => {
    removePaper(subjectId, [paper.paperId]);
    onRemoveOpenChange(false);
  }
  const handleDeletePaper = () => {
    deletePaper(paper.paperId, subjectId);
    onDeleteOpenChange(false);
  }

  const onDeleteOpenChange = useCallback((closed: boolean) => {
    setDeleteOpen(closed);
    setPaperName(paper.paperName);
  }, [paper.paperName])
  const onRemoveOpenChange = useCallback((closed: boolean) => {
    setRemoveOpen(closed);
    setPaperName(paper.paperName);
  }, [paper.paperName]);

  const router = useRouter();
  return (
    <div className={className}>
      <Link target='_blank' href={subjectId ? `/subjects/${subjectId}/${paper.paperId}` : `/papers/${paper.paperId}`}>
        <h4
          className="text-sm font-normal text-primary break-words space-x-1"
          title={paper.paperName ? `${paper.paperName}.pdf` : paper.paperName}
        >
          <PaperBadge className="-mt-[2px]" paper={paper} />
          <span className="hover:underline">{paper.extra.title || paper.paperName}</span>
        </h4>
      </Link>
      {
        (!paper.titleChinese && paper.titleZh) ? (
          <div className="mt-1 text-sm text-annotation-foreground break-all">{paper.titleZh}</div>
        ) : null
      }
      {
        !subjectId && paper.subjects.length ? (
          <div className="flex text-annotation-foreground text-xs overflow-hidden space-x-1 mt-2.5">
            {
              paper.subjects.slice(0, 2).map(({ subjectName, id }) => {
                return (
                  <Tip
                    key={`${paper.paperId}-${id}`}
                    label=""
                    side="bottom"
                    sideOffset={4}
                  >
                    <span
                      onClick={() => {
                        router.push(`/subjects/${id}`);
                      }}
                      className="flex-1 rounded-full px-2 border border-annotation-foreground truncate w-[80px] max-w-fit cursor-pointer hover:border-primary hover:text-primary"
                    >{subjectName}</span>
                  </Tip>
                )
              })
            }
            {
              paper.subjects.slice(2).length ? (
                <Tip
                  label={paper.subjects.slice(2).map(({ subjectName }) => subjectName).join('、')}
                  className="flex-none rounded-full px-2 border border-annotation-foreground min-w-[30px] cursor-pointer"
                  side="bottom"
                  align="start"
                  sideOffset={4}
                >
                  <span
                    style={{
                      letterSpacing: "-0.2em"
                    }}
                  >···&nbsp;</span>
                </Tip>
              ) : null
            }
          </div>
        ) : null
      }
      <div className="flex items-center justify-start gap-x-3 gap-y-4 mt-5 mb-2 whitespace-nowrap flex-wrap">
        {(!paper.titleChinese && paper.titleZh) ? (
          <Link className={cn(
            buttonVariants({ variant: "link" }),
            "cursor-pointer",
            linkClassName,
          )}
            target='_blank'
            href={subjectId ? `/subjects/${subjectId}/${paper.paperId}?trans=true` : `/papers/${paper.paperId}?trans=true`}
          >
            <BilingualIcon />
            <span>翻译</span>
          </Link>
        ) : null}
        <Link className={cn(
          buttonVariants({ variant: "link" }),
          "cursor-pointer",
          linkClassName,
        )}
          target='_blank'
          href={subjectId ? `/subjects/${subjectId}/${paper.paperId}` : `/papers/${paper.paperId}`}
        >
          <AIQAIcon />
          <span>问答</span>
        </Link>
        <ModifyPaperInfoButton
          withLoadingLabel={!(!Object.keys(paper.extra).length && paper.extractContent > 0)}
          paperId={paper.paperId}
          asyncConfirm={async () => {
            await fetchPapers(subjectId);
          }}
        >
          <div className={cn(
            buttonVariants({ variant: "link" }),
            "cursor-pointer",
            linkClassName,
          )}>
            <RenameIcon />
            <span>修正</span>
          </div>
        </ModifyPaperInfoButton>
        {
          subjectId ? (
            <Popover open={removeOpen} onOpenChange={onRemoveOpenChange}>
              <PopoverTrigger asChild>
                <Button variant="link" className={cn(
                  linkClassName,
                  "hover:text-destructive"
                )}>
                  <RemoveIcon />
                  <span>移除</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="grid gap-4">
                  <div className="flex flex-col">
                    <p className="text-sm break-all">
                      <span className="font-bold block">移除文献</span>
                      <>确认将文献《<span className="text-destructive ">{paperName}</span>》从主题分类中移除吗？</>
                    </p>
                    <Button size="sm" variant="destructive" className="self-end" onClick={() => handleRemovePaper()}>移除</Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : null
        }
        <Popover open={deleteOpen} onOpenChange={onDeleteOpenChange}>
          <PopoverTrigger asChild>
            <Button variant="link" className={cn(
              linkClassName,
              "hover:text-destructive"
            )}>
              <DeleteIcon />
              <span>删除</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="grid gap-4">
              <div className="flex flex-col">
                <p className="text-sm break-all">
                  <span className="font-bold block">删除文献</span>
                  <>确认删除文献《<span className="text-destructive ">{paperName}</span>》吗？</>
                </p>
                <Button size="sm" variant="destructive" className="self-end" onClick={() => handleDeletePaper()}>删除</Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}